import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import withAuth from '../../common/auth';
import { ToastContainer, toast } from 'react-toastify';
import { Slide } from 'react-toastify';
import { FiRefreshCcw, FiDownloadCloud, FiX, FiFileText, FiFile, FiSearch } from "react-icons/fi";
import { GET_SECTION_WISE_REPORT, BASE_URL } from '../../utlilities/config';
import axios from 'axios';
import { ReportEmbedContext } from '../reportEmbedContext';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
// Import and apply autoTable plugin
import autoTable from 'jspdf-autotable';


// Enhanced Spinner Component
const Spinner = () => (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
      <div className="flex flex-col items-center gap-3">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
        <p className="text-indigo-600 font-medium">Loading...</p>
      </div>
    </div>
);

// Helper function to format date in local timezone (IST)
const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatMonthYear = (dateValue) => {
  if (!dateValue) return '-';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return '-';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${year}`;
};

const formatInvoiceForPrint = (invoiceNo) => {
    const rawString = String(invoiceNo ?? '').trim();
    if (!rawString) return '';
    if (rawString.includes('/')) return rawString;

    const parsedInvoice = Number.parseInt(rawString, 10);
    const invoicePart = Number.isNaN(parsedInvoice)
        ? rawString
        : String(parsedInvoice).padStart(2, '0');
    const seriesName = String(localStorage.getItem('series_name') || '').trim();

    return seriesName ? `${invoicePart}/${seriesName}` : invoicePart;
};

const buildExportRows = (rows) => {
    const flat = [];
    rows.forEach((row) => {
        const itemDetails = Array.isArray(row.ItemDetails) && row.ItemDetails.length > 0
            ? row.ItemDetails
            : [{}];
        itemDetails.forEach((detail) => {
            flat.push({
                invoiceNo: formatInvoiceForPrint(row.InvoiceNo),
                entryDate: row.EntryDate ? new Date(row.EntryDate).toLocaleDateString('en-GB') : '-',
                patientName: row.PatientName || '-',
                doctorName: row.DoctorName || '-',
                billTotal: parseFloat(row.TotalAmount || 0).toFixed(2),
                itemName: detail.itemName || '-',
                batchCode: detail.batchCode || '-',
                qty: parseFloat(detail.qty || 0).toFixed(2),
                expiry: formatMonthYear(detail.expiryDate),
                batchOnhand: parseFloat(detail.batchOnhand || 0).toFixed(2),
            });
        });
    });
    return flat;
};

// Build initial filters
const buildInitialFilters = (filters = []) => {
  const today = new Date();
  const todayFormatted = formatLocalDate(today);

  return filters.reduce((accumulator, filter) => {
    if (filter.type === 'date') {
      // Set both fromDate and toDate to today by default
      const dateValue = todayFormatted;
      return { ...accumulator, [filter.id]: dateValue };
    }

    if (filter.type === 'select') {
      const defaultValue = filter.options?.[0]?.value ?? '';
      return { ...accumulator, [filter.id]: defaultValue };
    }

    if (filter.type === 'search') {
      return { ...accumulator, [filter.id]: '' };
    }

    return { ...accumulator, [filter.id]: '' };
  }, {});
};

// Report configuration
const reportConfig = {
  filters: [
    { id: 'fromDate', type: 'date', label: 'From Date' },
    { id: 'toDate', type: 'date', label: 'To Date' },
    { 
      id: 'scheduleCode', 
      type: 'select', 
      label: 'Schedule Code',
      options: [
        { value: 'Schedule H', label: 'Schedule H' },
        { value: 'Schedule H1', label: 'Schedule H1' },
        { value: 'Schedule X', label: 'Schedule X' },
      ]
    },
    { id: 'search', type: 'search', label: 'Search Patient Name' },
  ],
  table: {
    columns: [
      { key: 'InvoiceNo', label: 'Inv. No' },
      { key: 'EntryDate', label: 'Date' },
      { key: 'PatientName', label: 'Patient Name' },
      { key: 'TotalAmount', label: 'Total Amount' },
    ],
  },
};

// Export Format Selection Modal
const ExportFormatModal = ({ isOpen, onClose, onSelectFormat }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-96 max-w-md transform transition-transform duration-300 ease-out">
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex justify-between items-center rounded-t-2xl">
                    <h2 className="text-xl font-semibold text-white flex items-center">
                        <FiDownloadCloud className="mr-2" /> Export Report
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1 transition-colors duration-200"
                        aria-label="Close modal"
                    >
                        <FiX size={20} />
                    </button>
                </div>
                <div className="p-6">
                    <p className="text-sm text-slate-600 mb-4">Choose export format:</p>
                    <div className="flex flex-col gap-3">
                        <button
                            onClick={() => onSelectFormat('excel')}
                            className="flex items-center gap-3 p-4 rounded-lg border-2 border-slate-200 hover:border-indigo-500 hover:bg-indigo-50 transition-all duration-200 group"
                        >
                            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center group-hover:bg-green-200 transition-colors">
                                <FiFileText className="text-green-600" size={20} />
                            </div>
                            <div className="flex-1 text-left">
                                <p className="font-semibold text-slate-800">Export to Excel</p>
                                <p className="text-xs text-slate-500">Download as .xlsx file</p>
                            </div>
                        </button>
                        <button
                            onClick={() => onSelectFormat('pdf')}
                            className="flex items-center gap-3 p-4 rounded-lg border-2 border-slate-200 hover:border-indigo-500 hover:bg-indigo-50 transition-all duration-200 group"
                        >
                            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center group-hover:bg-red-200 transition-colors">
                                <FiFile className="text-red-600" size={20} />
                            </div>
                            <div className="flex-1 text-left">
                                <p className="font-semibold text-slate-800">Export to PDF</p>
                                <p className="text-xs text-slate-500">Download as .pdf file</p>
                            </div>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const SalesSectionWise = () => {
    const embedded = useContext(ReportEmbedContext);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState(() => buildInitialFilters(reportConfig.filters));
    const [salesData, setSalesData] = useState([]);
    const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
    const token = localStorage.getItem('accessToken');
    const [showExportModal, setShowExportModal] = useState(false);
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(1000); // Default 100 items per page


    const isElectron = () => {
        return window && window.process && window.process.type;
      };
    
      let ipcRenderer;
    
      if (isElectron()) {
          ipcRenderer = window.require('electron').ipcRenderer;
      }
    
    
    const handleFilterChange = (filterId, value) => {
        setFilters((prev) => ({
            ...prev,
            [filterId]: value,
        }));
    };

    const handleRunReport = () => {
        getSalesReport();
    };

    const getSalesReport = useCallback(async () => {
        setLoading(true);
        try {
            // Convert date strings to Date objects and format as YYYY-MM-DD
            const fromDate = filters.fromDate ? new Date(filters.fromDate) : null;
            const toDate = filters.toDate ? new Date(filters.toDate) : null;
            
            // Format date as YYYY-MM-DD
            const formatDate = (date) => {
                if (!date) return '';
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };
            
            // Validate scheduleCode
            if (!filters.scheduleCode) {
                toast.error('Please select a Schedule Code', { autoClose: 2000 });
                setLoading(false);
                return;
            }
            
            const response = await axios.get(`${BASE_URL}${GET_SECTION_WISE_REPORT}`, {
                headers: {
                    'XApiKey': process.env.REACT_APP_API_KEY,
                    'Authorization': `Bearer ${token}`,
                },
                params: {
                    'fromDate': formatDate(fromDate),
                    'toDate': formatDate(toDate),
                    'scheduleCode': filters.scheduleCode,
                },
            });
            if (response.status === 200) {
                const data = response.data.data || [];
                // Sort by InvoiceNo in descending order
                const sortedData = data.sort((a, b) => {
                    const invoiceA = a.InvoiceNo || '';
                    const invoiceB = b.InvoiceNo || '';
                    // Convert to number if possible, otherwise compare as strings
                    const numA = parseInt(invoiceA) || 0;
                    const numB = parseInt(invoiceB) || 0;
                    if (numA !== 0 || numB !== 0) {
                        return numB - numA; // Descending order
                    }
                    return invoiceB.localeCompare(invoiceA); // Descending order for strings
                });
                setSalesData(sortedData);
                setLastRefreshedAt(new Date());
                // Reset to first page when new data is loaded
                setCurrentPage(1);
            } else {
                toast.error('Failed to fetch sales report', { autoClose: 1000 });
            }
        } catch (error) {
            console.error('Failed to fetch sales report:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch sales report';
            toast.error(errorMessage, { autoClose: 2000 });
        }
        finally {
            setLoading(false);
        }
    }, [filters, token]);

    // Filter sales data based on search term
    const filteredSalesData = useMemo(() => {
        let filtered = salesData;

        // Filter by search term
        if (filters.search && filters.search.trim() !== '') {
            const searchTerm = filters.search.toLowerCase().trim();
            filtered = filtered.filter((row) => {
                const patientName = (row.PatientName || '').toLowerCase();
                const invoiceNo = (row.InvoiceNo || '').toString().toLowerCase();
                return patientName.includes(searchTerm) || 
                       invoiceNo.includes(searchTerm);
            });
        }

        return filtered;
    }, [salesData, filters.search]);

    // Pagination calculations
    const totalPages = Math.ceil(filteredSalesData.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedData = filteredSalesData.slice(startIndex, endIndex);

    const handleExport = () => {
        if (filteredSalesData.length === 0) {
            toast.error('No data to export', { autoClose: 2000 });
            return;
        }
        setShowExportModal(true);
    };

    const exportToExcel = () => {
        try {
            const exportRows = buildExportRows(filteredSalesData);
            const excelData = exportRows.map((r) => ({
                'Inv. No': r.invoiceNo,
                'Date': r.entryDate,
                'Patient': r.patientName,
                'Doctor': r.doctorName,
                'Bill Total': Number(parseFloat(r.billTotal || 0).toFixed(2)),
                'Item': r.itemName,
                'Batch': r.batchCode,
                'Qty': Number(parseFloat(r.qty || 0).toFixed(2)),
                'Expiry': r.expiry,
                'Batch Onhand': Number(parseFloat(r.batchOnhand || 0).toFixed(2)),
            }));

            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(excelData);

            ws['!cols'] = [
                { wch: 12 },
                { wch: 12 },
                { wch: 22 },
                { wch: 18 },
                { wch: 12 },
                { wch: 28 },
                { wch: 14 },
                { wch: 8 },
                { wch: 10 },
                { wch: 12 },
            ];

            XLSX.utils.book_append_sheet(wb, ws, 'Sales Section Wise');

            const fromDate = filters.fromDate ? new Date(filters.fromDate).toLocaleDateString('en-GB').replace(/\//g, '-') : 'All';
            const toDate = filters.toDate ? new Date(filters.toDate).toLocaleDateString('en-GB').replace(/\//g, '-') : 'All';
            const scheduleCode = filters.scheduleCode || 'All';
            const filename = `Sales_Section_Wise_${scheduleCode}_${fromDate}_to_${toDate}.xlsx`;

            XLSX.writeFile(wb, filename);
            toast.success('Excel file exported successfully', { autoClose: 2000 });
            setShowExportModal(false);
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            toast.error('Failed to export to Excel', { autoClose: 2000 });
        }
    };

    const exportToPDF = () => {
        try {
            const doc = new jsPDF('landscape', 'mm', 'a4');

            doc.setFontSize(16);
            doc.text('Sales Section Wise Report', 14, 15);

            doc.setFontSize(10);
            const fromDate = filters.fromDate ? new Date(filters.fromDate).toLocaleDateString('en-GB') : 'All';
            const toDate = filters.toDate ? new Date(filters.toDate).toLocaleDateString('en-GB') : 'All';
            const scheduleCode = filters.scheduleCode || 'All';
            doc.text(`Date Range: ${fromDate} to ${toDate}`, 14, 22);
            doc.text(`Schedule Code: ${scheduleCode}`, 14, 28);

            const exportRows = buildExportRows(filteredSalesData);
            const tableData = exportRows.map((r) => [
                r.invoiceNo,
                r.entryDate,
                r.patientName,
                r.doctorName,
                `₹${r.billTotal}`,
                r.itemName,
                r.batchCode,
                r.qty,
                r.expiry,
                r.batchOnhand,
            ]);

            autoTable(doc, {
                head: [[
                    'Inv. No',
                    'Date',
                    'Patient',
                    'Doctor',
                    'Bill Total',
                    'Item',
                    'Batch',
                    'Qty',
                    'Expiry',
                    'Batch Onhand',
                ]],
                body: tableData,
                startY: 35,
                styles: { fontSize: 7, cellPadding: 1.5 },
                headStyles: { fillColor: [99, 102, 241], textColor: 255, fontStyle: 'bold' },
                alternateRowStyles: { fillColor: [249, 250, 251] },
                margin: { top: 35, left: 10, right: 10 },
                columnStyles: {
                    0: { cellWidth: 22 },
                    1: { cellWidth: 20 },
                    2: { cellWidth: 28 },
                    3: { cellWidth: 26 },
                    4: { cellWidth: 22, halign: 'right' },
                    5: { cellWidth: 38 },
                    6: { cellWidth: 22 },
                    7: { cellWidth: 14, halign: 'right' },
                    8: { cellWidth: 16 },
                    9: { cellWidth: 18, halign: 'right' },
                },
            });

            const filename = `Sales_Section_Wise_${scheduleCode.replace(/\s+/g, '_')}_${fromDate.replace(/\//g, '-')}_to_${toDate.replace(/\//g, '-')}.pdf`;

            doc.save(filename);
            toast.success('PDF file exported successfully', { autoClose: 2000 });
            setShowExportModal(false);
        } catch (error) {
            console.error('Error exporting to PDF:', error);
            toast.error('Failed to export to PDF', { autoClose: 2000 });
        }
    };

    const handleExportFormat = (format) => {
        if (format === 'excel') {
            exportToExcel();
        } else if (format === 'pdf') {
            exportToPDF();
        }
    };

    // Reset to page 1 when filters change or new data is loaded
    useEffect(() => {
        setCurrentPage(1);
    }, [filters.search, salesData.length]);

    return (
        <div className="flex h-full bg-background">
            {/* <LeftMenu /> */}
            <ToastContainer
                position="top-center"
                autoClose={1000}
                limit={1}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
                transition={Slide}
            />
            
            {loading && <Spinner />}

            <ExportFormatModal
                isOpen={showExportModal}
                onClose={() => setShowExportModal(false)}
                onSelectFormat={handleExportFormat}
            />

            <div className="flex-1 flex flex-col h-full overflow-hidden">
                <header className="shrink-0 border-b border-slate-200 bg-white px-3 py-2.5 mb-2">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                        <div className="min-w-0 flex-1">
                            {!embedded && (
                                <div className="mb-2">
                                    <p className="text-xs font-semibold uppercase tracking-widest text-indigo-500">
                                        Sales Reports
                                    </p>
                                    <h1 className="text-lg font-bold text-slate-800">
                                        Sales Section Wise
                                    </h1>
                                </div>
                            )}
                            <form className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                                {reportConfig.filters.map((filter) => {
                                    if (filter.type === 'date') {
                                        return (
                                            <label key={filter.id} className="flex flex-col gap-1 text-xs font-medium text-slate-700">
                                                {filter.label}
                                                <input
                                                    type="date"
                                                    value={filters[filter.id] ?? ''}
                                                    onChange={(event) => handleFilterChange(filter.id, event.target.value)}
                                                    className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 transition"
                                                />
                                            </label>
                                        );
                                    }

                                    if (filter.type === 'select') {
                                        return (
                                            <label key={filter.id} className="flex flex-col gap-1 text-xs font-medium text-slate-700">
                                                {filter.label}
                                                <select
                                                    value={filters[filter.id] ?? ''}
                                                    onChange={(event) => handleFilterChange(filter.id, event.target.value)}
                                                    className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 transition"
                                                >
                                                    {(filter.options ?? []).map((option) => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </label>
                                        );
                                    }

                                    if (filter.type === 'search') {
                                        return (
                                            <label key={filter.id} className="flex flex-col gap-1 text-xs font-medium text-slate-700">
                                                {filter.label}
                                                <div className="relative">
                                                    <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
                                                    <input
                                                        type="text"
                                                        value={filters[filter.id] ?? ''}
                                                        onChange={(event) => handleFilterChange(filter.id, event.target.value)}
                                                        placeholder="Search by patient name or invoice..."
                                                        className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 transition"
                                                    />
                                                </div>
                                            </label>
                                        );
                                    }

                                    return null;
                                })}
                            </form>
                        </div>
                        <div className="flex flex-wrap items-center justify-start lg:justify-end gap-2 sm:gap-3">
                            <button
                                type="button"
                                onClick={handleRunReport}
                                className="inline-flex items-center gap-2 rounded-full bg-indigo-500 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-600"
                            >
                                <FiRefreshCcw size={16} />
                                Generate
                            </button>
                            <button
                                type="button"
                                onClick={() => setFilters(buildInitialFilters(reportConfig.filters))}
                                className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-indigo-300 hover:text-indigo-500"
                            >
                                Reset
                            </button>
                            <button
                                type="button"
                                onClick={handleExport}
                                className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-indigo-300 hover:text-indigo-500"
                            >
                                <FiDownloadCloud size={16} />
                                Export
                            </button>
                        </div>
                    </div>
                </header>

                <main className="flex-1 overflow-hidden flex flex-col">
                    <div className="flex flex-col gap-3 flex-1 min-h-0">
                        {/* Report Table Section */}
                        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm flex flex-col flex-1 min-h-0 overflow-auto">
                            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between flex-shrink-0 mb-4">
                                <div className="flex flex-col gap-2">
                                    <p className="text-sm text-slate-500">
                                        Showing <span className="font-semibold text-slate-700">{startIndex + 1}-{Math.min(endIndex, filteredSalesData.length)}</span> of <span className="font-semibold text-slate-700">{filteredSalesData.length}</span> {filters.search ? 'filtered' : ''} bills {filters.search && `out of ${salesData.length} total`} for this report.
                                        {filters.scheduleCode && (
                                            <span className="ml-2 px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded text-xs font-medium">
                                                {filters.scheduleCode}
                                            </span>
                                        )}
                                    </p>
                                    {/* Items per page selector */}
                                    <div className="flex items-center gap-2">
                                        <label className="text-xs text-slate-600">Items per page:</label>
                                        <select
                                            value={itemsPerPage}
                                            onChange={(e) => {
                                                setItemsPerPage(Number(e.target.value));
                                                setCurrentPage(1);
                                            }}
                                            className="rounded border border-slate-200 px-2 py-1 text-xs text-slate-700 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200"
                                        >
                                            <option value={25}>25</option>
                                            <option value={50}>50</option>
                                            <option value={100}>100</option>
                                            <option value={200}>200</option>
                                        </select>
                                    </div>
                                </div>
                                {/* Pagination Controls */}
                                {totalPages > 1 && (
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                            disabled={currentPage === 1}
                                            className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                                        >
                                            Previous
                                        </button>
                                        <div className="flex items-center gap-1">
                                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                                let pageNum;
                                                if (totalPages <= 5) {
                                                    pageNum = i + 1;
                                                } else if (currentPage <= 3) {
                                                    pageNum = i + 1;
                                                } else if (currentPage >= totalPages - 2) {
                                                    pageNum = totalPages - 4 + i;
                                                } else {
                                                    pageNum = currentPage - 2 + i;
                                                }
                                                return (
                                                    <button
                                                        key={pageNum}
                                                        onClick={() => setCurrentPage(pageNum)}
                                                        className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
                                                            currentPage === pageNum
                                                                ? 'bg-indigo-600 text-white border-indigo-600'
                                                                : 'border-slate-200 bg-white text-slate-700 hover:bg-indigo-50 hover:border-indigo-300'
                                                        }`}
                                                    >
                                                        {pageNum}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        <span className="text-sm text-slate-600">
                                            Page {currentPage} of {totalPages}
                                        </span>
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                            disabled={currentPage === totalPages}
                                            className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                                        >
                                            Next
                                        </button>
                                    </div>
                                )}
                            </div>
                            {filteredSalesData.length > 0 ? (
                                <div className="overflow-auto rounded-xl border border-slate-200">
                                    <table className="min-w-full divide-y divide-slate-200 text-xs">
                                        <thead className="bg-slate-100">
                                            <tr>
                                                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700">Inv. No</th>
                                                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700">Date</th>
                                                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700">Patient</th>
                                                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700">Doctor</th>
                                                <th className="px-3 py-2 text-right text-xs font-semibold text-slate-700">Bill Total</th>
                                                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700">Item</th>
                                                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700">Batch</th>
                                                <th className="px-3 py-2 text-right text-xs font-semibold text-slate-700">Qty</th>
                                                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700">Expiry</th>
                                                <th className="px-3 py-2 text-right text-xs font-semibold text-slate-700">Batch Onhand</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-200 bg-white">
                                            {paginatedData.map((row, index) => {
                                                const itemDetails = Array.isArray(row.ItemDetails) && row.ItemDetails.length > 0
                                                    ? row.ItemDetails
                                                    : [{}];
                                                const rowSpan = itemDetails.length;

                                                return itemDetails.map((detail, detailIndex) => (
                                                    <tr key={`${row.Sequence}_${row.InvoiceNo}_${detailIndex}`} className="hover:bg-indigo-50/30">
                                                        {detailIndex === 0 && (
                                                            <>
                                                                <td rowSpan={rowSpan} className="px-3 py-2 align-top font-medium text-slate-900">#{formatInvoiceForPrint(row.InvoiceNo)}</td>
                                                                <td rowSpan={rowSpan} className="px-3 py-2 align-top text-slate-700">{row.EntryDate ? new Date(row.EntryDate).toLocaleDateString('en-GB') : '-'}</td>
                                                                <td rowSpan={rowSpan} className="px-3 py-2 align-top text-slate-700">{row.PatientName || '-'}</td>
                                                                <td rowSpan={rowSpan} className="px-3 py-2 align-top text-slate-700">{row.DoctorName || '-'}</td>
                                                                <td rowSpan={rowSpan} className="px-3 py-2 align-top text-right font-semibold text-slate-900">₹{parseFloat(row.TotalAmount || 0).toFixed(2)}</td>
                                                            </>
                                                        )}
                                                        <td className="px-3 py-2 text-slate-700">{detail.itemName || '-'}</td>
                                                        <td className="px-3 py-2 text-slate-700">{detail.batchCode || '-'}</td>
                                                        <td className="px-3 py-2 text-right text-slate-700">{parseFloat(detail.qty || 0).toFixed(2)}</td>
                                                        <td className="px-3 py-2 text-slate-700">{formatMonthYear(detail.expiryDate)}</td>
                                                        <td className="px-3 py-2 text-right text-slate-700">{parseFloat(detail.batchOnhand || 0).toFixed(2)}</td>
                                                    </tr>
                                                ));
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-center py-12">
                                    <FiFileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                                    <p className="text-slate-500 text-lg">No data available for the selected period</p>
                                </div>
                            )}
                            {/* Bottom Pagination Controls */}
                            {filteredSalesData.length > 0 && totalPages > 1 && (
                                <div className="flex items-center justify-center gap-2 mt-6 pt-4 border-t border-slate-200">
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                        disabled={currentPage === 1}
                                        className="px-4 py-2 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                                    >
                                        Previous
                                    </button>
                                    <div className="flex items-center gap-1">
                                        {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                                            let pageNum;
                                            if (totalPages <= 7) {
                                                pageNum = i + 1;
                                            } else if (currentPage <= 4) {
                                                pageNum = i + 1;
                                            } else if (currentPage >= totalPages - 3) {
                                                pageNum = totalPages - 6 + i;
                                            } else {
                                                pageNum = currentPage - 3 + i;
                                            }
                                            return (
                                                <button
                                                    key={pageNum}
                                                    onClick={() => setCurrentPage(pageNum)}
                                                    className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                                                        currentPage === pageNum
                                                            ? 'bg-indigo-600 text-white border-indigo-600'
                                                            : 'border-slate-200 bg-white text-slate-700 hover:bg-indigo-50 hover:border-indigo-300'
                                                    }`}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <span className="text-sm text-slate-600 px-2">
                                        Page {currentPage} of {totalPages}
                                    </span>
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                        disabled={currentPage === totalPages}
                                        className="px-4 py-2 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </section>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default withAuth(SalesSectionWise);

